import React from 'react';
import { render, waitFor } from '@testing-library/react';
import { DatePicker, DateRangePicker } from '../../components';
import { TimezoneProvider } from '../TimezoneProvider';
import userEvent from '@testing-library/user-event';
import { DateTime } from 'luxon';

(window as any).ResizeObserver = class ResizeObserver {
	observe() {}
	disconnect() {}
};

describe('When DatePicker receives 2022-06-26T00:00:00.000-07:00', () => {
	beforeEach(() => jest.spyOn(console, 'error'));

	it('It should have selected date as 6/25 when timezone is set to Pacific/Honolulu', () => {
		const { getByPlaceholderText } = render(
			<TimezoneProvider timezone="Pacific/Honolulu">
				<DatePicker
					placeholder="Test DatePicker"
					value={new Date('2022-06-26T00:00:00.000-07:00')}
				/>
			</TimezoneProvider>
		);

		expect(getByPlaceholderText('Test DatePicker')).toHaveAttribute(
			'value',
			'06/25/2022'
		);
	});

	it('It should have selected date as 6/26 when timezone is set to America/Los_Angeles', async () => {
		const { getByPlaceholderText } = render(
			<TimezoneProvider timezone="America/Los_Angeles">
				<DatePicker
					placeholder="Test DatePicker"
					value={new Date('2022-06-26T00:00:00.000-07:00')}
				/>
			</TimezoneProvider>
		);

		await waitFor(() => {});

		expect(getByPlaceholderText('Test DatePicker')).toHaveAttribute(
			'value',
			'06/26/2022'
		);
	});
});

describe('When Today is clicked on the Datepicker', () => {
	beforeEach(() => jest.spyOn(console, 'error'));
	const onChangeHandler = jest.fn();
	const user = userEvent.setup();

	it('It should select today of current time in local timezone', async () => {
		const { getByPlaceholderText, getByText } = render(
			<TimezoneProvider timezone="America/Los_Angeles">
				<DatePicker
					placeholder="Test DatePicker"
					value={new Date('2022-06-26T00:00:00.000-07:00')}
					open
					onChange={onChangeHandler}
				/>
			</TimezoneProvider>
		);

		await waitFor(() => {});

		const localToday = DateTime.fromJSDate(new Date(), {
			zone: 'America/Los_Angeles',
		}).toFormat('LL/dd/yyyy');

		expect(getByPlaceholderText('Test DatePicker')).toHaveAttribute(
			'value',
			'06/26/2022'
		);

		await user.click(getByText('TODAY'));

		expect(getByPlaceholderText('Test DatePicker')).toHaveAttribute(
			'value',
			localToday
		);
	});
});

describe('When DateRangePicker receives 2022-06-26T00:00:00.000-07:00 for start and 2022-06-26T23:59:59.999-07:00 for end', () => {
	beforeEach(() => jest.spyOn(console, 'error'));

	it('It should have start as Jun 25 and end as Jun 26, 2022 when timezone is set to Pacific/Honolulu', () => {
		const { getByTestId } = render(
			<TimezoneProvider timezone="Pacific/Honolulu">
				<DateRangePicker
					data-testid="drp"
					value={{
						start: new Date('2022-06-26T00:00:00.000-07:00'),
						end: new Date('2022-06-26T23:59:59.999-07:00'),
					}}
				/>
			</TimezoneProvider>
		);

		expect(
			getByTestId('drp').getElementsByClassName('Trigger__value')[0]
				.innerHTML
		).toBe('Jun 25, 2022 - Jun 26, 2022');
	});

	it('It should have selected both start and end as Jun 26, 2022 when timezone is set to America/Los_Angeles', () => {
		const { getByTestId } = render(
			<TimezoneProvider timezone="America/Los_Angeles">
				<DateRangePicker
					data-testid="drp"
					value={{
						start: new Date('2022-06-26T00:00:00.000-07:00'),
						end: new Date('2022-06-26T23:59:59.999-07:00'),
					}}
				/>
			</TimezoneProvider>
		);

		expect(
			getByTestId('drp').getElementsByClassName('Trigger__value')[0]
				.innerHTML
		).toBe('Jun 26, 2022 - Jun 26, 2022');
	});
});

const getNewToday = DateTime.fromJSDate(new Date(), {
	zone: 'America/Los_Angeles',
})
	.setLocale('en')
	.toLocaleString({
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
	});

describe('When Today is clicked on the DateRangePicker', () => {
	beforeEach(() => jest.spyOn(console, 'error'));

	const user = userEvent.setup();

	it('It should select today of current time in local timezone', async () => {
		const { getByPlaceholderText, getByText } = render(
			<TimezoneProvider timezone="America/Los_Angeles">
				<DateRangePicker
					value={{
						start: new Date('2022-06-26T00:00:00.000-07:00'),
						end: new Date('2022-06-27T00:00:00.000-07:00'),
					}}
					open
				/>
			</TimezoneProvider>
		);

		await waitFor(() => {});

		expect(getByPlaceholderText('Start Date')).toHaveAttribute(
			'value',
			'06/26/2022'
		);

		expect(getByPlaceholderText('End Date')).toHaveAttribute(
			'value',
			'06/27/2022'
		);

		await user.click(getByText('TODAY'));

		expect(getByPlaceholderText('Start Date')).toHaveAttribute(
			'value',
			getNewToday
		);

		expect(getByPlaceholderText('End Date')).toHaveAttribute(
			'value',
			undefined
		);

		await user.click(getByText('TODAY'));

		expect(getByPlaceholderText('Start Date')).toHaveAttribute(
			'value',
			getNewToday
		);

		expect(getByPlaceholderText('End Date')).toHaveAttribute(
			'value',
			getNewToday
		);
	});

	it('It should swap dates if start date is populated, today is selected, and dates are out of order', async () => {
		const { getByPlaceholderText, getByText } = render(
			<TimezoneProvider timezone="America/Los_Angeles">
				<DateRangePicker
					value={{
						start: new Date('2122-06-26T00:00:00.000-07:00'),
						end: undefined,
					}}
					open
				/>
			</TimezoneProvider>
		);

		await waitFor(() => {});

		expect(getByPlaceholderText('Start Date')).toHaveAttribute(
			'value',
			'06/26/2122'
		);

		expect(getByPlaceholderText('End Date')).toHaveAttribute(
			'value',
			undefined
		);

		await user.click(getByText('TODAY'));

		expect(getByPlaceholderText('Start Date')).toHaveAttribute(
			'value',
			getNewToday
		);

		expect(getByPlaceholderText('End Date')).toHaveAttribute(
			'value',
			'06/26/2122'
		);
	});

	it('It should swap dates if end date is populated, today is selected, and dates are out of order', async () => {
		const { getByPlaceholderText, getByText } = render(
			<TimezoneProvider timezone="America/Los_Angeles">
				<DateRangePicker
					value={{
						start: undefined,
						end: new Date('2022-06-26T00:00:00.000-07:00'),
					}}
					open
				/>
			</TimezoneProvider>
		);

		await waitFor(() => {});

		expect(getByPlaceholderText('Start Date')).toHaveAttribute(
			'value',
			undefined
		);

		expect(getByPlaceholderText('End Date')).toHaveAttribute(
			'value',
			'06/26/2022'
		);

		await user.click(getByText('TODAY'));

		expect(getByPlaceholderText('Start Date')).toHaveAttribute(
			'value',
			'06/26/2022'
		);

		expect(getByPlaceholderText('End Date')).toHaveAttribute(
			'value',
			getNewToday
		);
	});
});
